import { useState } from "react";
import { isFullHex, looksLikeHex, normalizeHex } from "../theme/color";

/**
 * LE CODE HEXADÉCIMAL QUI SE TAPE — dans la ligne du panneau, ET dans le sélecteur.
 *
 * ⚠️ EXTRAIT PARCE QU'IL Y EN A DEUX. Le code se tape à côté de la pastille, et il se
 * tape aussi DANS la fenêtre du sélecteur, là où la fenêtre native n'offrait que trois
 * cases R, G, B. Deux copies d'une même mécanique, c'est la seconde qui oublie la règle
 * des six chiffres le jour où on touche à la première — et la règle est précisément ce
 * qui empêche l'événement de clignoter pendant la frappe (voir plus bas).
 *
 * ⚠️ CE QUI EST EN COURS DE FRAPPE NE S'ENREGISTRE PAS. Le panneau applique en mémoire
 * à chaque écriture et la page se repeint sous les doigts. Un demi-code n'est pas une
 * couleur, et `ContrastMeter` le jetterait ; pire, « #91b » EN EST UNE (`#9911bb`) — en
 * route vers « #91bc8f », elle repeindrait tout l'événement en violet le temps de trois
 * touches. Le brouillon reste donc local tant que le code n'est pas complet, et les
 * codes courts attendent le point final.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function HexInput({
  value,
  label,
  placeholder,
  className,
  onChange,
}: {
  /** Ce qui est rangé sous la clé — un hexadécimal, ou rien. */
  value: string;
  /** Le nom du réglage : le champ s'annonce « Couleur principale (code hexadécimal) ». */
  label: string;
  /** Ce que dit le champ vide : « aucune », « d'après l'image ». */
  placeholder: string;
  className: string;
  onChange: (hex: string) => void;
}) {
  /** Le code en cours de frappe. `null` = personne ne tape, le champ montre ce qui est rangé. */
  const [draft, setDraft] = useState<string | null>(null);
  /* Signalé seulement quand la frappe ne peut plus aboutir — pas à « #91b ». */
  const illisible =
    draft !== null && draft.trim() !== "" && !looksLikeHex(draft);

  return (
    <input
      type="text"
      className={className}
      /* Le brouillon tant qu'on tape, sinon ce qui est rangé — tel qu'il est rangé. */
      value={draft ?? value}
      placeholder={placeholder}
      aria-label={`${label} (code hexadécimal)`}
      aria-invalid={illisible || undefined}
      spellCheck={false}
      autoComplete="off"
      /* Entrée, c'est « j'ai fini » — le geste qu'on fait après avoir tapé un code,
         et le panneau n'a aucun formulaire pour l'entendre. Sans ça, un code à
         trois chiffres resterait à l'écran sans jamais être posé. */
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        /* ⚠️ SIX CHIFFRES, PAS TROIS — voir `isFullHex` : « #91b », traversé en
           route vers « #91bc8f », est un code valide qui repeindrait tout
           l'événement en violet pendant la frappe. */
        if (isFullHex(raw)) onChange(normalizeHex(raw)!);
      }}
      /* Le point final : le code court y est pris, et ce qui n'est pas une couleur
         n'en devient pas une — le champ reprend la valeur rangée plutôt que de
         garder un code mort à l'écran. Retirer la couleur, c'est « Reprendre
         l'héritée » (voir `Provenance`) ou le bouton passé en `children`. */
      onBlur={() => {
        const hex = draft === null ? null : normalizeHex(draft);
        /* La même couleur ne se réécrit pas : sortir d'un champ qu'on n'a fait
           qu'effleurer salirait le panneau et relancerait un enregistrement pour
           rien. Comparé normalisé, une valeur reprise en majuscules de la V1 ne
           compte pas non plus comme un changement. */
        if (hex && hex !== normalizeHex(value)) onChange(hex);
        setDraft(null);
      }}
    />
  );
}
